import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router';
import { api } from '@/lib/api';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  // Source of truth for the token's purpose: query the server with the token
  // to find out whether it's a forgot-password reset or a portal invitation.
  // The URL hint (?purpose=invite) is used as the initial value so newer
  // links flip the copy instantly without waiting for the round-trip; the
  // server response then confirms or corrects it (handles legacy links sent
  // before the URL hint was wired).
  const [isInvite, setIsInvite] = React.useState<boolean>(
    searchParams.get('purpose') === 'invite',
  );
  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api<{ purpose: string | null }>(`/auth/magic-link/info?token=${encodeURIComponent(token)}`);
        if (!cancelled && r.success) {
          setIsInvite(r.data?.purpose === 'invite');
        }
      } catch {
        // Network / 404 — keep the URL-hint default.
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Centralised copy keeps the JSX uncluttered and ensures button / heading
  // / loading / success-message stay in sync.
  const copy = isInvite ? {
    heading:   'Set up your password',
    subhead:   'Choose a password to finish setting up your Atlas Fleet account.',
    submit:    'Set up password',
    submitting:'Setting up…',
    success:   'Account set up successfully. Please sign in.',
  } : {
    heading:   'Set new password',
    subhead:   null,
    submit:    'Reset password',
    submitting:'Resetting…',
    success:   'Password reset successfully. Please sign in.',
  };

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await api('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword: password }),
      });
      if (res.success) {
        navigate('/auth/login', { state: { message: copy.success } });
      } else {
        setError(res.error?.message || (isInvite ? 'Setup failed' : 'Reset failed'));
      }
    } catch (err: any) {
      setError(err.message || (isInvite ? 'Setup failed' : 'Reset failed'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="text-center space-y-4">
          <p className="text-destructive">Invalid reset link. No token provided.</p>
          <Link to="/auth/login" className="text-sm text-primary hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold font-heading text-foreground">{copy.heading}</h1>
          {copy.subhead && (
            <p className="text-sm text-muted-foreground">{copy.subhead}</p>
          )}
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {isInvite ? 'Password' : 'New Password'}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Confirm Password</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? copy.submitting : copy.submit}
          </button>
        </form>
      </div>
    </div>
  );
}
